import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../api/api";
import { useNotifications } from "../context/NotificationContext.js";
import { useIsMobile } from "../Hooks/useIsMobile";
import { useNavigate, Link, useLocation } from "react-router-dom";
import "../styles/Nav.css";

const logo = `${process.env.PUBLIC_URL}/logo4.png`;

function Nav() {
  const { user, logout } = useAuth();
  const {
    hasNewServiceTasks,
    hasIncomingOffers,
    hasUpdatedMyOffers,
    hasNewJobs,
    deleteNotification,
    clearAllNotifications,
    unreadCount,
    notifications,
    markAllNotificationsAsRead,
    markNotificationAsRead,
    markAsRead,
    notificationsEnabled,
  } = useNotifications();
  const location = useLocation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const searchWrapperRef = useRef(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    const closeDropdown = (e) => {
      if (!e.target.closest('.notification-wrapper')) {
        setShowNotifications(false);
      }
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('click', closeDropdown);
    return () => document.removeEventListener('click', closeDropdown);
  }, []);

  const handleKeyDown = (e) => {
    if (!searchQuery) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev < searchResults.length - 1 ? prev + 1 : prev));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === "Enter") {
      if (activeIndex >= 0 && searchResults[activeIndex]) {
        const r = searchResults[activeIndex];
        navigate(`/#serviceId-${r.id}`);
        setSearchQuery("");
        setShowSearchResults(false);
        setActiveIndex(-1);
      }
    } else if (e.key === "Escape") {
      setShowSearchResults(false);
      setActiveIndex(-1);
    }
  };

  useEffect(() => {
    setActiveIndex(-1);
  }, [searchQuery]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    if (diff < 24 * 60 * 60 * 1000) {
      return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
  };

  const buildAvailableTasksLink = (tab, relatedId) => {
    const params = new URLSearchParams({ tab });
    if (relatedId !== null && relatedId !== undefined) {
      params.set('relatedId', String(relatedId));
    }
    return `/availableTasks?${params.toString()}`;
  };

  const isOfferRefreshNotification = (notification) => {
    const title = String(notification?.title || '').toLowerCase();
    const msg = String(notification?.message || '').toLowerCase();
    return title.includes('teklifinizi yenileyin') || msg.includes('yenileme gerekli');
  };

  const isCancellationRequestNotification = (notification) => {
    const title = String(notification?.title || '').toLowerCase();
    const msg = String(notification?.message || '').toLowerCase();
    return title.includes('iptal iste') || msg.includes('iptal iste');
  };

  const getNotificationTarget = (notification) => {
    if (!notification) return null;

    if (notification.type === 'offer_accepted') return '/myJobs';
    if (notification.type === 'new_chat_message') {
      return `/myJobs?openChat=${notification.relatedId}`;
    }
    if (isCancellationRequestNotification(notification) && notification.relatedId) {
      return `/myJobs?openCancel=${notification.relatedId}`;
    }
    if (notification.type === 'new_offer_group' || notification.type === 'new_offer') {
      return buildAvailableTasksLink('offers', notification.relatedId);
    }
    if (notification.type === 'offer_updated') {
      const tab = isOfferRefreshNotification(notification) ? 'myOffers' : 'offers';
      return buildAvailableTasksLink(tab, notification.relatedId);
    }
    if (notification.type === 'offer_rejected') {
      return buildAvailableTasksLink('myOffers', notification.relatedId);
    }
    if (notification.type === 'offer_deleted') {
      return buildAvailableTasksLink('offers', notification.relatedId);
    }
    if (notification.type === 'task_closed') return '/userProfile';
    if (notification.type?.includes('review')) return '/userProfile';
    if (notification.type === 'new_task') {
      return buildAvailableTasksLink('tasks', notification.relatedId);
    }
    if (notification.type?.includes('offer')) {
      return buildAvailableTasksLink('offers', notification.relatedId);
    }
    return null;
  };

  const handleNotificationClick = (notification) => {
    if (!notification) return;

    if (!notification.isRead) {
      if (notification.type === 'new_offer_group') {
        const groupObjs = notification.originalIds.map(id => ({ id, isRead: false }));
        markAsRead('group', groupObjs);
      } else {
        markNotificationAsRead(notification.id);
      }
    }

    const target = getNotificationTarget(notification);
    if (target) navigate(target);
    setShowNotifications(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(`/services/getServicewithCategory`);
        setCategories(res.data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchData();
  }, []);

  // Tour Highlighting Logic
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tourKey = params.get('tour');
    if (tourKey) {
      setTimeout(() => {
        const element = document.querySelector(`[data-tour="${tourKey}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.classList.add('tour-highlight-pulse');
          setTimeout(() => element.classList.remove('tour-highlight-pulse'), 5000);
        }
      }, 500);
    }
  }, [location.search]);

  const searchResults = categories.reduce((acc, cat) => {
    const query = (searchQuery || "").toLowerCase();
    if (!query) return acc;
    cat.services.forEach((service) => {
      const sName = (service.name || "").toLowerCase();
      const cName = (cat.categoryName || "").toLowerCase();
      if (sName.includes(query) || cName.includes(query)) {
        acc.push({
          ...service,
          parentCategory: cat,
          displayTitle: `${cat.categoryName} / ${service.name}`,
        });
      }
    });
    return acc;
  }, []);

  return (
    <>
      <div className="mobile-topbar brand">
        <Link to="/" onClick={() => setMobileMenuOpen(false)}>
          <img src={logo} alt="logo" className="mobile-logo" />
          <div className="brand-title">
            <div className="Bi">Bi</div>
            <div className="Teklifle">Teklifle</div>
            </div>
        </Link>

        {!isMobile && (
          <div className="nav-center">
            <div id="search-bar" data-tour="search-bar" ref={searchWrapperRef}>
              <input
                type="text"
                placeholder="Hizmet Ara..."
                value={searchQuery}
                onKeyDown={handleKeyDown}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowSearchResults(true);
                }}
              />
              {searchQuery && showSearchResults && (
                <div className="search-results-dropdown">
                  {searchResults.map((r, index) => (
                    <div
                      key={r.id}
                      className={`dropdown-item ${index === activeIndex ? "active" : ""}`}
                      onClick={() => {
                        navigate(`/#serviceId-${r.id}`);
                        setSearchQuery("");
                        setShowSearchResults(false);
                        setActiveIndex(-1);
                      }}
                      onMouseEnter={() => setActiveIndex(index)}
                    >
                      {r.displayTitle}
                    </div>
                  ))}
                  {searchResults.length === 0 && <div className="dropdown-item no-result">Sonuç bulunamadı</div>}
                </div>
              )}
            </div>
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          {user && (
            <div className="notification-wrapper mobile-notif">
              <button
                className="navBTN icon-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowNotifications(!showNotifications);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px'
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '26px', color: 'var(--primary-color)' }}>notifications</span>
                {notificationsEnabled && unreadCount > 0 && <span className="notification-badge mobile-badge">{unreadCount}</span>}
              </button>

              {showNotifications && (
                <div className="notification-dropdown mobile-dropdown">
                  <div className="notif-header">
                    <span>Bildirimler</span>
                    <div className="notif-header-actions">
                      {unreadCount > 0 && (
                        <button onClick={markAllNotificationsAsRead} className="mark-all-btn">
                          Tumu Okundu
                        </button>
                      )}
                      {notifications.length > 0 && (
                        <button onClick={clearAllNotifications} className="clear-all-btn">
                          Tumunu Sil
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="notif-list">
                    {notifications.length === 0 ? (
                      <div className="no-notif">Hiç bildiriminiz yok.</div>
                    ) : (
                      notifications.map(n => (
                        <div
                          key={n.id}
                          className={`notif-item ${!n.isRead ? 'unread' : ''}`}
                          onClick={() => handleNotificationClick(n)}
                        >
                          <div className="notif-icon">
                            {(n.type === 'new_offer' || n.type === 'new_offer_group') && '🏷️'}
                            {n.type === 'offer_accepted' && '🎉'}
                            {n.type === 'offer_rejected' && '❌'}
                            {n.type === 'offer_updated' && '📝'}
                            {n.type === 'new_task' && '📌'}
                            {n.type === 'task_closed' && '⏳'}
                            {n.type === 'new_chat_message' && '💬'}
                            {n.type === 'other' && '📢'}
                          </div>
                          <div className="notif-content">
                            <div className="notif-title">{n.title}</div>
                            <div className="notif-msg">{n.message}</div>
                            <div className="notif-date">{formatDate(n.createdAt)}</div>
                          </div>
                          {n.type !== 'new_offer_group' && (
                            <button
                              className="delete-notif-btn"
                              onClick={(e) => deleteNotification(n.id, e)}
                              title="Sil"
                            >
                              <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>delete</span>
                            </button>
                          )}
                          {!n.isRead && <div className="notif-dot"></div>}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <button className="hamburger" data-tour="nav-menu" onClick={() => setMobileMenuOpen(true)}>
            <span className="material-symbols-outlined">menu</span>
            {notificationsEnabled && (hasNewServiceTasks || hasIncomingOffers || hasUpdatedMyOffers || hasNewJobs) && (
              <span className="notification-dot"></span>
            )}
          </button>
        </div>
      </div>

      <div
        className={`side-nav-overlay ${mobileMenuOpen ? "open" : ""}`}
        onClick={() => setMobileMenuOpen(false)}
      />
      <div className={`side-nav ${mobileMenuOpen ? "open" : ""}`}>
        <div className="side-nav-header">
          <div className="side-nav-user-info">
            <img src={logo} alt="logo" className="side-logo-img" />
            <div className="user-greeting">
              {user ? (
                <>
                  <span className="welcome-text">Hoş geldin,</span>
                  <span className="user-name-text">{user.username}</span>
                </>
              ) : (
                <span>Bi Teklifle</span>
              )}
            </div>
          </div>
          <button className="close-side-nav" onClick={() => setMobileMenuOpen(false)}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <nav className="side-nav-links">
          {user ? (
            <>
              <Link to="/#contact-us" onClick={() => setMobileMenuOpen(false)} className="side-feedback-btn">
                <span className="material-symbols-outlined">forum</span>
                Görüş & Öneri
              </Link>
              {user.isAdmin && <Link to="/admin" onClick={() => setMobileMenuOpen(false)}>Admin Panel</Link>}
              <Link to="/myJobs" data-tour="nav-jobs" onClick={() => setMobileMenuOpen(false)}>
                İşlerim
                {notificationsEnabled && hasNewJobs && <span className="notification-dot"></span>}
              </Link>
              <Link id="userJobLink" data-tour="nav-offers" to="/availableTasks" onClick={() => setMobileMenuOpen(false)}>
                Tekliflerim
                {notificationsEnabled && (hasNewServiceTasks || hasIncomingOffers || hasUpdatedMyOffers) && location.pathname !== "/availableTasks" && (
                  <span className="notification-dot"></span>
                )}
              </Link>
              <Link to="/userProfile" data-tour="nav-profile" onClick={() => setMobileMenuOpen(false)}>Hesabım</Link>
              <Link to="/payments" onClick={() => setMobileMenuOpen(false)}>Ödemelerim</Link>
              <Link to="/settings" data-tour="nav-settings" onClick={() => setMobileMenuOpen(false)}>Ayarlar</Link>
              <button
                className="side-logout"
                onClick={() => {
                  logout();
                  navigate("/");
                  setMobileMenuOpen(false);
                }}
              >
                Çıkış Yap
              </button>
            </>
          ) : (
            <>
              <Link to="/Login" data-tour="nav-login" onClick={() => setMobileMenuOpen(false)}>Giriş</Link>
              <Link to="/Register" data-tour="nav-register" onClick={() => setMobileMenuOpen(false)} className="side-register-btn">
                Yeni Hesap
              </Link>
            </>
          )}
        </nav>
      </div>

      {/* {!isMobile && (
        <nav className="navbar desktop-nav">
          <div className="nav-left">
            <div className="brand" onClick={() => { navigate('/#home-hero') }}>
              <Link to="/">
                <img src={logo} alt="logo" />
                <span>Bi Teklifle</span>
              </Link>
            </div>
            <div className="quick-links">
              <Link className="navBTN feedback-shortcut" to="/#contact-us">
                <span className="material-symbols-outlined">forum</span>
                Görüş & Öneri
              </Link>
              <Link className="navBTN" to="/#about-us">Hakkımızda</Link>
              <Link className="navBTN" to="/#contact-us">İletişim</Link>
            </div>
          </div>

          <div className="nav-right">
            {user ? (
              <>
                <div className="notification-wrapper" style={{ position: 'relative', marginRight: '15px' }}>
                  <button
                    className="navBTN icon-btn"
                    onClick={() => setShowNotifications(!showNotifications)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', position: 'relative', display: 'flex', alignItems: 'center' }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: '28px' }}>notifications</span>
                    {notificationsEnabled && unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
                  </button>

                  {showNotifications && (
                    <div className="notification-dropdown">
                      <div className="notif-header">
                        <span>Bildirimler</span>
                        <div className="notif-header-actions">
                          {unreadCount > 0 && (
                            <button onClick={markAllNotificationsAsRead} className="mark-all-btn">
                              Tumu Okundu
                            </button>
                          )}
                          {notifications.length > 0 && (
                            <button onClick={clearAllNotifications} className="clear-all-btn">
                              Tumunu Sil
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="notif-list">
                        {notifications.length === 0 ? (
                          <div className="no-notif">Hiç bildiriminiz yok.</div>
                        ) : (
                          notifications.map(n => (
                            <div
                              key={n.id}
                              className={`notif-item ${!n.isRead ? 'unread' : ''}`}
                              onClick={() => handleNotificationClick(n)}
                            >
                              <div className="notif-icon">
                                {(n.type === 'new_offer' || n.type === 'new_offer_group') && '🏷️'}
                                {n.type === 'offer_accepted' && '🎉'}
                                {n.type === 'offer_rejected' && '❌'}
                                {n.type === 'offer_updated' && '📝'}
                                {n.type === 'new_task' && '📌'}
                                {n.type === 'task_closed' && '⏳'}
                                {n.type === 'new_chat_message' && '💬'}
                                {n.type === 'other' && '📢'}
                              </div>
                              <div className="notif-content">
                                <div className="notif-title">{n.title}</div>
                                <div className="notif-msg">{n.message}</div>
                                <div className="notif-date">{formatDate(n.createdAt)}</div>
                              </div>
                              {n.type !== 'new_offer_group' && (
                                <button
                                  className="delete-notif-btn"
                                  onClick={(e) => deleteNotification(n.id, e)}
                                  title="Sil"
                                >
                                  <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>delete</span>
                                </button>
                              )}
                              {!n.isRead && <div className="notif-dot"></div>}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {user.isAdmin && <Link className="navBTN" to="/admin">Admin Panel</Link>}
                <Link className="navBTN" to="/myJobs" style={{ position: 'relative' }}>
                  İşlerim
                  {notificationsEnabled && hasNewJobs && location.pathname !== "/myJobs" && <span className="notification-dot"></span>}
                </Link>
                <Link id="userJobLink" className="navBTN" to="/availableTasks" style={{ position: 'relative' }}>
                  Tekliflerim
                  {notificationsEnabled && (hasNewServiceTasks || hasIncomingOffers || hasUpdatedMyOffers) && location.pathname !== "/availableTasks" && <span className="notification-dot"></span>}
                </Link>
                <Link className="navBTN" to="/payments" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <span className="material-symbols-outlined">payments</span>
                  <span>Ödemelerim</span>
                </Link>
                <Link className="navBTN" to="/settings" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <span className="material-symbols-outlined">settings</span>
                  <span>Ayarlar</span>
                </Link>
                <Link className="navBTN profile-nav-btn" to="/userProfile">
                  <span className="material-symbols-outlined">account_circle</span>
                  <span className="nav-username">{user.username}</span>
                </Link>
                <button className="navBTN logout-btn" onClick={() => { logout(); navigate("/"); }}>
                  <span className="material-symbols-outlined">logout</span>
                  <span>Çıkış</span>
                </button>
              </>
            ) : (
              <>
                <Link className="navBTN" to="/Login">Giriş</Link>
                <Link className="navBTN register-btn" to="/Register">Yeni Hesap</Link>
              </>
            )}
          </div>
        </nav>
      )} */}
    </>
  );
}

export default Nav;
